/**
 * KnowledgeCard — Pure domain model (no React, no API imports).
 *
 * Represents a single knowledge asset (note, document, file URL,
 * or markdown import) stored in the knowledge graph.
 */

export type KnowledgeSourceType = 'note' | 'document' | 'file_url' | 'markdown_import';

export class KnowledgeCard {
  constructor(
    readonly cardId: string,
    readonly clientId: string,
    readonly projectId: string | null,
    readonly sourceType: KnowledgeSourceType,
    readonly title: string,
    readonly sourceUrl: string | null,
    readonly summaryText: string | null,
    /** Short subtitle preview chosen by the backend for list views. */
    readonly previewText: string | null,
    readonly ingestionStatus: string,
    readonly tags: string[],
    readonly createdAt: string,
    readonly updatedAt: string,
  ) {}

  /** Human-readable label for the source type. */
  getTypeLabel(): string {
    const labels: Record<KnowledgeSourceType, string> = {
      note: 'Note',
      document: 'Document',
      file_url: 'File',
      markdown_import: 'Import',
    };
    return labels[this.sourceType];
  }

  /** File extension inferred from source_url, or null. */
  getFileExtension(): string | null {
    /**
     * Infer a “document type” extension for UI badges.
     *
     * Why this is non-trivial:
     * - Direct file URLs end in `.../file.pptx`.
     * - SharePoint `webUrl` can be a “viewer route” like `AllItems.aspx?...` where the real file
     *   name lives in query params (commonly `id=/.../MyDeck.pptx`).
     *
     * Strategy:
     * 1) Prefer a real extension in the URL path.
     * 2) If the path ends in a viewer route (`.aspx`, `.html`), try known query params (`id`,
     *    `file`, `filename`, `name`) and extract from decoded values.
     * 3) Fall back to scanning the decoded URL string for known extensions.
     */
    if (!this.sourceUrl) {
      return null;
    }
    const ALLOWED = new Set(['DOCX', 'PPTX', 'PDF', 'XLSX', 'CSV', 'MD']);
    const IGNORED_PATH_EXT = new Set(['ASPX', 'HTML', 'HTM']);

    const extFromPath = (pathValue: string): string | null => {
      const match = /\.([a-zA-Z0-9]+)$/.exec(pathValue);
      if (!match) {
        return null;
      }
      const ext = match[1].toUpperCase();
      return ALLOWED.has(ext) ? ext : null;
    };

    const extFromString = (value: string): string | null => {
      const match = /\.(docx|pptx|pdf|xlsx|csv|md)(?:\b|%|&|\?|$)/i.exec(value);
      return match ? match[1].toUpperCase() : null;
    };

    try {
      const parsed = new URL(this.sourceUrl);

      // Prefer a real file extension from the path.
      const pathExtMatch = /\.([a-zA-Z0-9]+)$/.exec(parsed.pathname);
      if (pathExtMatch) {
        const ext = pathExtMatch[1].toUpperCase();
        if (!IGNORED_PATH_EXT.has(ext) && ALLOWED.has(ext)) {
          return ext;
        }
      }

      // SharePoint often uses AllItems.aspx with the real file path in query params (id/file/name).
      const paramsToCheck = ['id', 'file', 'filename', 'name'];
      for (const key of paramsToCheck) {
        const raw = parsed.searchParams.get(key);
        if (!raw) {
          continue;
        }
        const decoded = decodeURIComponent(raw);
        const extracted = extFromPath(decoded) ?? extFromString(decoded);
        if (extracted) {
          return extracted;
        }
      }

      // Fallback: scan the full URL string for known extensions (handles encoded `%2Epptx` cases).
      const decodedUrl = decodeURIComponent(this.sourceUrl);
      return extFromString(decodedUrl);
    } catch {
      // If parsing fails, keep the legacy behavior as a last resort.
      return extFromString(this.sourceUrl);
    }
  }

  /** Secondary display text: truncated summary or source URL. */
  getSubtitle(maxLength: number = 80): string {
    const preview = this.previewText?.trim();
    if (preview) {
      return preview.length > maxLength ? preview.slice(0, maxLength) + '...' : preview;
    }
    if (this.sourceUrl) {
      return this.sourceUrl.length > maxLength
        ? this.sourceUrl.slice(0, maxLength) + '...'
        : this.sourceUrl;
    }
    return '';
  }

  /** Whether the asset has finished ingestion processing. */
  isReady(): boolean {
    return this.ingestionStatus === 'completed';
  }

  /** Whether this is an editable note (vs. a read-only ingested document). */
  isNote(): boolean {
    return this.sourceType === 'note';
  }
}
